import React, { useEffect, useState } from "react";
import { FormikContextType, useField } from "formik";
import { FieldRenderers, FormField } from "@avamae/formbuilder";
import { useFormikContext } from "formik";
import { Dropdown, SelectOption } from "components/dropdown/Dropdown";
// import { setDefaultValuesId } from "reducers/defaultValues";
import { useDispatch, useSelector } from "react-redux";

import { FormFieldTooltip } from "components/helpers/FormFieldTooltip/FormFieldTooltip";
import {
  MultiDropdown,
  MultiSelectOption,
} from "components/dropdown/MultiDropdown";

import tickIcon from "assets/Icon_Tick_01.png";
import { pascalCaseToCamelCase } from "helpers/pascalCaseToCamelCase";
import { APP_ROUTES, FILTER_KEY } from "config";
import { useNavigate } from "react-router-dom";

import editIcon from "assets/edit_icon.png";

const tooltipMainIconSource = require("assets/Tooltip.png");
const tooltipCloseIconSource = require("assets/Icon_Btn_06.png");

const capitaliseFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export type SingleSelectOption = {
  label: string;
  value: number | string;
};

type Option = {
  label: string;
  value: { id: number };
};

function EditSingleSelect(props: {
  isMulti?: boolean;
  name: string;
  dataSource: string | null;
  showUseDefaultValues?: boolean;
  useDefaultValue?: boolean;
  limitOptionsToDefaultValue?: boolean;
  type?: string | null;
  valueTypeStringFields?: string[];
  onChangeFunction?: OnChangeFunction;
  portalMode?: boolean | undefined;
  readOnlyMode?: boolean;
  isClearable?: boolean;
}) {
  const [field] = useField<SingleSelectOption>(props.name);
  const [options, setOptions] = useState<SelectOption[] | null>(null);
  const [selectedOption, setSelectedOption] = useState<SelectOption | null>(
    null
  );
  const [initialOption, setInitialOption] = useState<{
    label: string;
    value: number | string | null;
  }>({
    label: "",
    value: null,
  });
  const [data, setData] = useState<any>(null);
  const [defaultValue, setDefaultValue] = useState<any>(null);
  const [relevantFormikError, setRelevantFormikError] = useState<string | null>(
    null
  );
  const [additionalLanguageOption, setAdditionalLanguageOption] = useState<
    number | null
  >(null);

  const fieldAcceptsStringValues =
    props.valueTypeStringFields &&
    props.valueTypeStringFields.includes(field.name);

  const formik = useFormikContext<SingleSelectOption>();
  const formikErrors = formik.errors;

  useEffect(() => {
    if (formikErrors.hasOwnProperty(field.name)) {
      const error = formikErrors[field.name as keyof typeof formikErrors];

      if (typeof error === "string") {
        setRelevantFormikError(error);
      }
    } else {
      setRelevantFormikError(null);
    }
  }, [formikErrors]);

  useEffect(() => {
    let tempInitialOption: { label: string; value: number | string | null } = {
      label: "",
      value: null,
    };

    if (field && typeof field === "object" && field.hasOwnProperty("value")) {
      if (typeof field.value === "number") {
        tempInitialOption["value"] = field.value;
      } else if (
        field &&
        typeof field === "object" &&
        field.hasOwnProperty("value") &&
        typeof field.value === "string" &&
        fieldAcceptsStringValues
      ) {
        tempInitialOption["value"] = field.value;
      }
    }

    const dataSource = props.dataSource;

    if (
      formik &&
      formik.values &&
      typeof formik.values === "object" &&
      dataSource &&
      formik.values.hasOwnProperty(dataSource) &&
      Array.isArray(formik.values[dataSource as keyof typeof formik.values])
    ) {
      const data = formik.values[dataSource as keyof typeof formik.values];

      if (data && Array.isArray(data)) {
        const tempOptions = data.map(
          (item: SingleSelectOption, idx: number) => {
            if (
              typeof tempInitialOption.value === "number" &&
              item.value === tempInitialOption.value
            ) {
              tempInitialOption["label"] = item.label;
            } else if (
              fieldAcceptsStringValues &&
              typeof tempInitialOption.value === "string" &&
              item.value === tempInitialOption.value
            ) {
              tempInitialOption["label"] = item.label;
            }

            return {
              label: item.label,

              value: item.value,
            };
          }
        );

        setOptions(tempOptions);
        setData(data);
        setInitialOption(tempInitialOption);

        if (props.useDefaultValue) {
          const defaultValueFieldName = `default${capitaliseFirstLetter(
            field.name
          )}`;

          if (formik.values.hasOwnProperty(defaultValueFieldName)) {
            const defaultValueData: any =
              formik.values[
                defaultValueFieldName as keyof typeof formik.values
              ];

            const dataSourceMatch = data.filter(
              item => item.value && item.value === defaultValueData
            );

            if (
              dataSourceMatch.length === 1 &&
              typeof dataSourceMatch[0] === "object" &&
              dataSourceMatch[0].hasOwnProperty("label") &&
              dataSourceMatch[0].hasOwnProperty("value")
            ) {
              const tempDefaultValue = {
                label: dataSourceMatch[0].label,
                value: dataSourceMatch[0].value,
              };

              setDefaultValue(tempDefaultValue);

              // Only use default value if we haven't already set an initial option from previously selected options above
              if (!tempInitialOption.value) {
                setInitialOption(tempDefaultValue);
              }
            }
          }
        }
      }
    } else if (
      formik &&
      formik.values &&
      typeof formik.values === "object" &&
      dataSource &&
      formik.values.hasOwnProperty(dataSource)
    ) {
      setOptions([]);
    }

    if (
      tempInitialOption.label !== "" &&
      typeof tempInitialOption.value === "number" &&
      selectedOption === null
    ) {
      setSelectedOption(tempInitialOption);
    } else if (
      fieldAcceptsStringValues &&
      tempInitialOption.label !== "" &&
      typeof tempInitialOption.value === "string" &&
      selectedOption === null
    ) {
      setSelectedOption(tempInitialOption);
    }
  }, []);

  useEffect(() => {
    if (
      initialOption &&
      initialOption.label !== "" &&
      initialOption.value !== null &&
      !selectedOption
    ) {
      setSelectedOption(initialOption);
      formik.setFieldValue(field.name, initialOption.value);
    }
  }, [initialOption]);

  useEffect(() => {
    if (defaultValue) {
      if (props.limitOptionsToDefaultValue) {
        setOptions([defaultValue]);

        if (
          initialOption.label !== defaultValue.label &&
          initialOption.value !== defaultValue.value
        ) {
          setOptions([defaultValue, initialOption]);
        } else {
          setOptions([defaultValue]);
        }
      }

      if (!selectedOption) {
        setSelectedOption(defaultValue);
        formik.setFieldValue(field.name, defaultValue.value);
      }
    }
  }, [defaultValue]);

  // Make sure field value is in the list of options, if not, set to default value or empty value
  useEffect(() => {
    if (
      options &&
      Array.isArray(options) &&
      options.length > 0 &&
      options[0].hasOwnProperty("value") &&
      field.value
    ) {
      const valueMatch = options.filter(item => item.value === field.value);

      if (valueMatch.length === 0) {
        if (defaultValue) {
          setSelectedOption(defaultValue);
          if (defaultValue.hasOwnProperty("value")) {
            formik.setFieldValue(field.name, defaultValue.value);
          }
        } else {
          setSelectedOption({
            label: "",
            value: null,
          });
          formik.setFieldValue(field.name, null);
        }
      }
    }
  }, [field]);

  const onChange = (v: MultiSelectOption | null) => {
    if (!v) {
      // User cleared the selection via the X button (only reachable when isClearable=true)
      setSelectedOption(null);
      formik.setFieldValue(field.name, null);

      if (props.onChangeFunction) {
        props.onChangeFunction(null, formik);
      }
      return;
    }

    setSelectedOption(v);
    let finalValue = v.value;
    if (!fieldAcceptsStringValues && typeof v.value === "string") {
      const tempValue = parseInt(v.value);

      if (!isNaN(tempValue)) {
        finalValue = parseInt(finalValue);
      }
    }
    formik.setFieldValue(field.name, finalValue);

    if (props.onChangeFunction) {
      props.onChangeFunction(finalValue, formik);
    }
  };

  if (options) {
    return (
      <div
        className={`EditSingleSelectFieldContainer ${
          formik.submitCount > 0 && relevantFormikError ? "Error" : ""
        } ${props.readOnlyMode ? "EditReadOnly" : ""}`}
      >
        <Dropdown
          options={options}
          value={selectedOption ? selectedOption : field.value}
          onChange={onChange}
          customClassName="EditSingleSelect"
          customNoOptionsMessage="No options"
          portalMode={props.portalMode}
          isClearable={props.isClearable}
        />

        {props.showUseDefaultValues && (
          <div
            className={`UseDefaultValuesBtn Btn ${
              !selectedOption ? "Disabled" : ""
            }`}
            onClick={() => {
              if (selectedOption && selectedOption.value) {
              }
            }}
          >
            <div className="Text">Use Default Values</div>
          </div>
        )}

        {formik.submitCount > 0 && relevantFormikError && (
          <div className="ErrorLabel">{relevantFormikError}</div>
        )}
      </div>
    );
  } else {
    return (
      <div
        className={`EditSingleSelectFieldContainer ${
          formik.submitCount > 0 && relevantFormikError ? "Error" : ""
        }`}
      >
        <div className="EditBox ViewOnly NoOptions">
          <div className="Text">Error: No data</div>
        </div>

        {formik.submitCount > 0 && relevantFormikError && (
          <div className="ErrorLabel">{relevantFormikError}</div>
        )}
      </div>
    );
  }
}

type MultiOption = {
  [key: string]: any;
};

function EditMultiSelect(props: {
  isMulti?: boolean;
  name: string;
  dataSource: string | null;
  showUseDefaultValues?: boolean;
  showUseRoleValues?: boolean;
  useDefaultValue?: boolean;
  onChangeFunction?: OnChangeFunction;
  onClear?: OnClearFunction;
  portalMode?: boolean;
  readOnlyMode?: boolean;
}) {
  const [options, setOptions] = useState<MultiSelectOption[] | null>(null);
  const [initialOptions, setInitialOptions] = useState<any | null>(null);
  const [fieldOriginalData, setFieldOriginalData] = useState<any>(null);
  const [field] = useField<MultiSelectOption>(props.name);
  const [selectedOptions, setSelectedOptions] = useState<
    MultiSelectOption[] | null
  >(null);
  const [disableSaveButton, setDisableSaveButton] = useState<boolean>(true);
  const [defaultSelectedValue, setDefaultSelectedValue] = useState<any | null>(
    null
  );
  const [relevantFormikError, setRelevantFormikError] = useState<string | null>(
    null
  );

  const dispatch = useDispatch();

  const formik = useFormikContext<MultiSelectOption>();
  const formikErrors = formik.errors;

  const onClearFunction =
    props.onClear && formik
      ? () => {
          //@ts-ignore
          props.onClear(formik);
        }
      : undefined;

  useEffect(() => {
    if (formikErrors.hasOwnProperty(field.name)) {
      const error = formikErrors[field.name as keyof typeof formikErrors];

      if (typeof error === "string") {
        setRelevantFormikError(error);
      }
    } else {
      setRelevantFormikError(null);
    }
  }, [formikErrors]);

  const dataSource = props.dataSource;

  useEffect(() => {
    let tempInitialOptions: any = [];

    if (
      field &&
      typeof field === "object" &&
      field.hasOwnProperty("value") &&
      Array.isArray(field.value)
    ) {
      tempInitialOptions = [...field.value];
    }

    if (
      formik &&
      formik.values &&
      typeof formik.values === "object" &&
      dataSource &&
      formik.values.hasOwnProperty(dataSource) &&
      Array.isArray(formik.values[dataSource as keyof typeof formik.values]) &&
      options === null
    ) {
      const data = formik.values[dataSource as keyof typeof formik.values];

      if (data && Array.isArray(data)) {
        setFieldOriginalData(data);

        const newOptions = data.map((item: MultiOption, idx: number) => {
          if (Array.isArray(tempInitialOptions) && item.id) {
            const finalTempInitialOptions = tempInitialOptions.filter(
              (item2: any) => item2.bHasAccess === true
            );

            setInitialOptions(finalTempInitialOptions);
          }

          return {
            label: item.roleName ? item.roleName : item.label,
            value: item.id ? item.id : item.value,
          };
        });

        setOptions(newOptions);

        if (
          Array.isArray(tempInitialOptions) &&
          tempInitialOptions.length > 0
        ) {
          const alreadySelectedOptions = newOptions.filter(item =>
            tempInitialOptions.includes(item.value)
          );

          if (
            alreadySelectedOptions &&
            Array.isArray(alreadySelectedOptions) &&
            alreadySelectedOptions.length > 0
          ) {
            setSelectedOptions(alreadySelectedOptions);
          }
        }
      }
    } else if (
      formik &&
      formik.values &&
      typeof formik.values === "object" &&
      dataSource &&
      formik.values.hasOwnProperty(dataSource)
    ) {
      setOptions([]);
    }
  }, []);

  useEffect(() => {
    if (initialOptions) {
      formik.setFieldValue(field.name, initialOptions);
      const parsedInitialOptions = initialOptions.map((item: any) => {
        return { label: item.roleName, value: item.id };
      });

      setSelectedOptions(parsedInitialOptions);
    }
    if (defaultSelectedValue && !selectedOptions) {
      setSelectedOptions([defaultSelectedValue]);
    }
  }, [initialOptions, defaultSelectedValue, options]);

  const parseOption = (option: { label: string; value: any }) => {
    let parsedOption = option;
    if (typeof parsedOption.value === "string") {
      parsedOption = {
        label: parsedOption.label,
        value: parseInt(parsedOption.value),
      };
    }

    return parsedOption;
  };

  const onChange = (v: MultiSelectOption[]) => {
    let value: MultiSelectOption[] | null = v;
    let submitValue: MultiSelectOption[] | null =
      v && Array.isArray(v) ? v.map(item => item.value) : null;

    if (value === null) {
      value = [];
    }

    setSelectedOptions(value);

    formik.setFieldValue(field.name, submitValue);

    if (props.onChangeFunction) {
      props.onChangeFunction(submitValue, formik);
    }

    setDisableSaveButton(false);
  };
  useEffect(() => {
    const selected = selectedOptions?.map(({ value }) => value);
    formik.setFieldValue(props.name, selected);
  }, [selectedOptions]);

  if (options) {
    return (
      <div
        className={`EditMultiSelectFieldContainer ${
          formik.submitCount > 0 && relevantFormikError ? "Error" : ""
        } ${props.readOnlyMode ? "EditReadOnly" : ""}`}
      >
        <MultiDropdown
          options={options}
          value={selectedOptions ? selectedOptions : null}
          onChange={onChange}
          customClassName="EditMultiSelect"
          customNoOptionsMessage="No options"
          isSearchable={false}
          onClear={onClearFunction}
          portalMode={props.portalMode}
          styles={{
            multiValue: (styles: any) => {
              return {
                ...styles,
                backgroundColor: "#DEEBFF",
                color: "#4C9AFF",
              };
            },
            multiValueLabel: (styles: any) => ({
              ...styles,
              color: "#4C9AFF",
              fontSize: "1.1rem",
            }),
            multiValueRemove: (styles: any) => ({
              ...styles,
              color: "#4C9AFF",
              ":hover": {
                backgroundColor: "red",
                color: "white",
              },
            }),
          }}
        />

        {props.showUseDefaultValues && (
          <div
            className={`UseDefaultValuesBtn Btn ${
              !selectedOptions ? "Disabled" : ""
            }`}
            onClick={() => {
              if (selectedOptions) {
              }
            }}
          >
            <div className="Text">Use Default Values</div>
          </div>
        )}

        {props.showUseRoleValues && (
          <div
            className={`UseDefaultValuesBtn Btn ${
              !selectedOptions || disableSaveButton ? "Disabled" : ""
            }`}
            onClick={() => {
              if (selectedOptions) {
                const data = fieldOriginalData;

                if (
                  data &&
                  Array.isArray(data) &&
                  data.length > 0 &&
                  data[0].hasOwnProperty("id")
                ) {
                  let selectedOptionsIds: number[] = [];

                  if (
                    selectedOptions &&
                    Array.isArray(selectedOptions) &&
                    selectedOptions.length > 0 &&
                    selectedOptions[0].hasOwnProperty("value")
                  ) {
                  }
                  selectedOptionsIds = selectedOptions.map(item => {
                    if (item.value === "string") {
                      return parseInt(item.value);
                    } else {
                      return item.value;
                    }
                  });

                  const finalData = data.map(item => {
                    if (selectedOptionsIds.includes(item.id)) {
                      return { ...item, bHasAccess: true };
                    } else {
                      return { ...item, bHasAccess: false };
                    }
                  });

                  const filteredData = finalData.filter(
                    item => item.bHasAccess === true
                  );

                  formik.setFieldValue(field.name, finalData);

                  setDisableSaveButton(true);
                }
              }
            }}
          >
            <div className="Text">Set admin user roles</div>
          </div>
        )}

        {formik.submitCount > 0 && relevantFormikError && (
          <div className="ErrorLabel">{relevantFormikError}</div>
        )}
      </div>
    );
  } else {
    return (
      <div
        className={`EditSingleSelectFieldContainer ${
          formik.submitCount > 0 && relevantFormikError ? "Error" : ""
        }`}
      >
        <div className="EditBox ViewOnly NoOptions">
          <div className="Text">Error: No data</div>
        </div>

        {formik.submitCount > 1 && relevantFormikError && (
          <div className="ErrorLabel">{relevantFormikError}</div>
        )}
      </div>
    );
  }
}

function EditSingleSelectRadio(props: {
  isMulti?: boolean;
  name: string;
  dataSource: string | null;
  showUseDefaultValues?: boolean;
  showUseRoleValues?: boolean;
  useDefaultValue?: boolean;
}) {
  const [options, setOptions] = useState<MultiSelectOption[] | null>(null);
  const [initialOptions, setInitialOptions] = useState<any | null>(null);
  const [fieldOriginalData, setFieldOriginalData] = useState<any>(null);
  const [field] = useField<MultiSelectOption>(props.name);
  const [
    selectedOption,
    setSelectedOption,
  ] = useState<MultiSelectOption | null>(null);
  const [disableSaveButton, setDisableSaveButton] = useState<boolean>(true);
  const [defaultSelectedValue, setDefaultSelectedValue] = useState<any | null>(
    null
  );
  const [relevantFormikError, setRelevantFormikError] = useState<string | null>(
    null
  );

  const dispatch = useDispatch();

  const formik = useFormikContext<MultiSelectOption>();
  const formikErrors = formik.errors;

  useEffect(() => {
    if (formikErrors.hasOwnProperty(field.name)) {
      const error = formikErrors[field.name as keyof typeof formikErrors];

      if (typeof error === "string") {
        setRelevantFormikError(error);
      }
    } else {
      setRelevantFormikError(null);
    }
  }, [formikErrors]);

  const dataSource = props.dataSource;

  useEffect(() => {
    let tempInitialOptions: any = [];

    if (
      field &&
      typeof field === "object" &&
      field.hasOwnProperty("value") &&
      Array.isArray(field.value)
    ) {
      tempInitialOptions = [...field.value];
    }

    if (
      formik &&
      formik.values &&
      typeof formik.values === "object" &&
      dataSource &&
      formik.values.hasOwnProperty(dataSource) &&
      Array.isArray(formik.values[dataSource as keyof typeof formik.values]) &&
      options === null
    ) {
      const data = formik.values[dataSource as keyof typeof formik.values];

      if (data && Array.isArray(data)) {
        setFieldOriginalData(data);

        const newOptions = data.map((item: MultiOption, idx: number) => {
          if (Array.isArray(tempInitialOptions) && item.id) {
            const finalTempInitialOptions = tempInitialOptions.filter(
              (item2: any) => item2.bHasAccess === true
            );

            setInitialOptions(finalTempInitialOptions);
          }

          return {
            label: item.roleName ? item.roleName : item.label,
            value: item.id ? item.id : item.value,
          };
        });

        setOptions(newOptions);

        const currentValue = field.value;

        const currentOption = newOptions
          .filter(item => item.value === currentValue)
          .filter(item => item !== null);

        if (currentOption.length === 1) {
          setSelectedOption(currentOption[0]);
        }
      }
    } else if (
      formik &&
      formik.values &&
      typeof formik.values === "object" &&
      dataSource &&
      formik.values.hasOwnProperty(dataSource)
    ) {
      setOptions([]);
    }
  }, []);

  useEffect(() => {
    if (initialOptions) {
      formik.setFieldValue(field.name, initialOptions);
      const parsedInitialOptions = initialOptions.map((item: any) => {
        return { label: item.roleName, value: item.id };
      });

      setSelectedOption(parsedInitialOptions);
    }

    if (defaultSelectedValue && !selectedOption) {
      setSelectedOption(defaultSelectedValue);
    }
  }, [initialOptions, defaultSelectedValue]);

  const parseOption = (option: { label: string; value: any }) => {
    let parsedOption = option;
    if (typeof parsedOption.value === "string") {
      parsedOption = {
        label: parsedOption.label,
        value: parseInt(parsedOption.value),
      };
    }

    return parsedOption;
  };

  const onChange = (v: any) => {
    setSelectedOption(v);

    setDisableSaveButton(false);

    formik.setFieldValue(field.name, v.value);
  };

  if (options) {
    return (
      <div
        className={`EditSingleSelectRadioContainer ${
          formik.submitCount > 0 && relevantFormikError ? "Error" : ""
        }`}
      >
        <div className="Options">
          {options.map((item, idx) => {
            const selected =
              selectedOption && item.value === selectedOption.value;

            return (
              <div
                className={`Option ${selected ? "Active" : ""}`}
                onClick={() => onChange(item)}
                key={`option-${field.name}-${idx}`}
              >
                <div
                  className="ImageContainer"
                  key={`option-image-container-${field.name}-${idx}`}
                >
                  {selected && (
                    <img
                      src={tickIcon}
                      className="Image"
                      alt="tick"
                      key={`option-image-containter-check-box--${field.name}-${idx}`}
                    />
                  )}
                </div>

                <div
                  className="Label"
                  key={`option-label-${field.name}-${idx}`}
                >
                  {item.label}
                </div>
              </div>
            );
          })}
        </div>

        {props.showUseDefaultValues && (
          <div
            className={`UseDefaultValuesBtn Btn ${
              !selectedOption ? "Disabled" : ""
            }`}
            onClick={() => {
              if (selectedOption) {
              }
            }}
          >
            <div className="Text">Use Default Values</div>
          </div>
        )}

        {formik.submitCount > 0 && relevantFormikError && (
          <div className="ErrorLabel">{relevantFormikError}</div>
        )}
      </div>
    );
  } else {
    return (
      <div
        className={`EditSingleSelectFieldContainer ${
          formik.submitCount > 0 && relevantFormikError ? "Error" : ""
        }`}
      >
        <div className="EditBox ViewOnly NoOptions">
          <div className="Text">Error: No data</div>
        </div>

        {formik.submitCount > 0 && relevantFormikError && (
          <div className="ErrorLabel">{relevantFormikError}</div>
        )}
      </div>
    );
  }
}

export type OnChangeFunction = (
  newValue: any,
  formik:
    | FormikContextType<SingleSelectOption>
    | FormikContextType<MultiSelectOption>
) => void;

export type OnChangeFunctions = {
  [fieldName: string]: OnChangeFunction;
};

export type OnClearFunction = (
  formik:
    | FormikContextType<SingleSelectOption>
    | FormikContextType<MultiSelectOption>
) => void;

export type OnClearFunctions = {
  [fieldName: string]: OnClearFunction;
};

export const singleMultiSelectFieldRenderer = (
  type: "singleselect" | "multiselect" | "singleselectradio" | "file",
  valueTypeStringFields?: string[],
  onChangeFunctions?: OnChangeFunctions,
  onClearFunctions?: OnClearFunctions,
  portalMode?: boolean,
  readOnlyMode?: boolean,
  showEditIcon?: boolean,
  clearableFields?: string[]
) =>
  function singleMultiSelectFieldRenderer<T>(
    _key: React.ReactText,
    field: FormField<T>
  ) {
    const onChangeFunction =
      onChangeFunctions && onChangeFunctions.hasOwnProperty(field.name)
        ? onChangeFunctions[field.name as keyof typeof onChangeFunctions]
        : undefined;

    const onClearFunction =
      onClearFunctions && onClearFunctions.hasOwnProperty(field.name)
        ? onClearFunctions[field.name as keyof typeof onClearFunctions]
        : undefined;

    if (type === "singleselect") {
      return (
        <div key={`${String(field.name)}`} className="FormBox FormSideBySide">
          <div className="FormLabel">
            <label>{field.label}</label>

            {showEditIcon && (
              <img src={editIcon} alt="edit icon" className="EditIcon" />
            )}
          </div>
          <div className="FormField">
            <EditSingleSelect
              name={String(field.name)}
              dataSource={
                field.dataSource && typeof field.dataSource === "string"
                  ? pascalCaseToCamelCase(field.dataSource)
                  : null
              }
              showUseDefaultValues={field.name === "languages" ? true : false}
              useDefaultValue={true}
              limitOptionsToDefaultValue={true}
              type={field.type ? field.type : null}
              valueTypeStringFields={valueTypeStringFields}
              onChangeFunction={onChangeFunction}
              portalMode={portalMode}
              readOnlyMode={readOnlyMode}
              isClearable={clearableFields?.includes(String(field.name))}
            />
          </div>
        </div>
      );
    } else if (type === "multiselect") {
      return (
        <div key={`${String(field.name)}`} className="FormBox FormSideBySide">
          <div className="FormLabel">
            <label>{field.label}</label>

            {showEditIcon && (
              <img src={editIcon} alt="edit icon" className="EditIcon" />
            )}
          </div>
          <div className="FormField">
            <EditMultiSelect
              name={String(field.name)}
              dataSource={
                field.dataSource && typeof field.dataSource === "string"
                  ? pascalCaseToCamelCase(field.dataSource)
                  : null
              }
              showUseRoleValues={field.name === "adminUserRoles" ? true : false}
              useDefaultValue={true}
              onChangeFunction={onChangeFunction}
              onClear={onClearFunction}
              portalMode={portalMode}
              readOnlyMode={readOnlyMode}
            />
          </div>
        </div>
      );
    } else if (type === "singleselectradio") {
      return (
        <div key={`${String(field.name)}`} className="FormBox FormSideBySide">
          <div className="FormLabel">
            <label>{field.label}</label>

            {showEditIcon && (
              <img src={editIcon} alt="edit icon" className="EditIcon" />
            )}
          </div>
          <div className="FormField">
            <EditSingleSelectRadio
              name={String(field.name)}
              dataSource={
                field.dataSource && typeof field.dataSource === "string"
                  ? pascalCaseToCamelCase(field.dataSource)
                  : null
              }
              showUseRoleValues={field.name === "adminUserRoles" ? true : false}
              useDefaultValue={true}
            />
          </div>
        </div>
      );
    } else {
      return (
        <div key={`${String(field.name)}`} className="FormBox FormSideBySide">
          <div className="FormLabel">
            <label>{field.label}</label>

            {showEditIcon && (
              <img src={editIcon} alt="edit icon" className="EditIcon" />
            )}
          </div>
          <div className="FormField">
            <div className="EditBox ViewOnly">-</div>
          </div>
        </div>
      );
    }
  };

type EditMultiSelectReadOnlyProps = {
  name: string;
  dataSource: string | null;
  showUseDefaultValues?: boolean;
  showUseRoleValues?: boolean;
  linkConfig?: LinkConfig;
  additionalComponent?: AdditionalComponent;
  additionalComponentSelectedIdFieldName?: string;
};

const EditMultiSelectReadOnly: React.FC<EditMultiSelectReadOnlyProps> = ({
  name,
  dataSource,
  showUseDefaultValues,
  showUseRoleValues,
  linkConfig,
  additionalComponent,
  additionalComponentSelectedIdFieldName,
}) => {
  const [field] = useField<any>(name);
  const formik = useFormikContext<any>();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  let options: MultiSelectOption[] | null = null;

  let values: any = null;

  if (field && field.hasOwnProperty("value")) {
    values = field.value;
  }

  useEffect(() => {
    if (
      additionalComponentSelectedIdFieldName &&
      typeof formik.values === "object" &&
      formik.values.hasOwnProperty(additionalComponentSelectedIdFieldName)
    ) {
      setSelectedId(`${formik.values[additionalComponentSelectedIdFieldName]}`);
    }
  }, [formik.values]);

  const handleLinkOnClick = () => {
    const navigate = useNavigate();
    const id =
      formik.values &&
      linkConfig &&
      linkConfig.filterValueFieldName &&
      formik.values.hasOwnProperty(linkConfig.filterValueFieldName)
        ? formik.values[
            linkConfig?.filterValueFieldName as keyof typeof formik.values
          ]
        : null;

    if (
      id &&
      linkConfig &&
      linkConfig.filterFieldName &&
      linkConfig.filterOperator &&
      linkConfig.route
    ) {
      let filterString = `${linkConfig.filterFieldName} ${linkConfig.filterOperator} (${id})`;

      window.localStorage.setItem(FILTER_KEY, filterString);
      navigate(linkConfig.route);
    }
  };

  return (
    <>
      {linkConfig &&
        linkConfig.filterFieldName &&
        linkConfig.text &&
        linkConfig.route && (
          <div className="LinkContainer">
            <div className="Link" onClick={handleLinkOnClick}>
              {linkConfig.text}
            </div>
          </div>
        )}
      <div className="EditBox ViewOnly EditFieldRenderer EditMultiSelectReadOnly">
        {values && Array.isArray(values) && values.length > 0 ? (
          Array.isArray(values) &&
          values.length > 0 &&
          values.map((item, i) => {
            return (
              <div className="Item" key={`item-${i}-${name}`}>
                <div className="Label">
                  {item && item.label ? item.label : ""}
                </div>
              </div>
            );
          })
        ) : (
          <div className="NoItemsData">-</div>
        )}
      </div>

      {additionalComponent && selectedId && additionalComponent(selectedId)}
    </>
  );
};

export type AdditionalComponent = (
  selectedId: string | null,
  disable?: boolean
) => React.ReactNode;

export type AdditionalComponents = {
  [fieldName: string]: {
    component: AdditionalComponent;
    selectedIdFieldName: string;
  };
};

type EditSingleSelectReadOnlyProps = {
  name: string;
  dataSource: string | null;
  valueTypeStringFields?: string[];
  additionalComponent?: AdditionalComponent;
  additionalComponentSelectedIdFieldName?: string;
};

/**
 * Read-only counterpart to EditSingleSelect: resolves a scalar field value against the form's
 * embedded option set and renders the matched label as static text.
 *
 * EditMultiSelectReadOnly cannot stand in for this. It renders `field.value` as an array of
 * {label} objects, so a single-select field — whose value is a bare id or string — fails its
 * Array.isArray check and falls through to "-". A form whose fields are all single-selects
 * therefore renders entirely blank in view mode.
 */
const EditSingleSelectReadOnly: React.FC<EditSingleSelectReadOnlyProps> = ({
  name,
  dataSource,
  valueTypeStringFields,
  additionalComponent,
  additionalComponentSelectedIdFieldName,
}) => {
  const [field] = useField<any>(name);
  const formik = useFormikContext<any>();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Same contract as EditMultiSelectReadOnly: the additional component is driven by a separate
  // id field on the form, not by this field's own value.
  useEffect(() => {
    if (
      additionalComponentSelectedIdFieldName &&
      typeof formik.values === "object" &&
      formik.values.hasOwnProperty(additionalComponentSelectedIdFieldName)
    ) {
      setSelectedId(`${formik.values[additionalComponentSelectedIdFieldName]}`);
    }
  }, [formik.values]);

  // Mirrors EditSingleSelect: numeric values always match, string values only for fields the
  // caller has declared string-valued. Keep the two in step.
  const fieldAcceptsStringValues = Boolean(
    valueTypeStringFields && valueTypeStringFields.includes(name)
  );

  const value = field && field.hasOwnProperty("value") ? field.value : null;

  const isMatchableValue =
    typeof value === "number" ||
    (fieldAcceptsStringValues && typeof value === "string");

  let label: string | null = null;

  if (
    isMatchableValue &&
    dataSource &&
    formik.values &&
    typeof formik.values === "object" &&
    formik.values.hasOwnProperty(dataSource)
  ) {
    const options = formik.values[dataSource as keyof typeof formik.values];

    if (Array.isArray(options)) {
      const match = options.find(
        (item: SingleSelectOption) => item && item.value === value
      );

      if (match && match.label) {
        label = match.label;
      }
    }
  }

  return (
    <>
      <div className="EditBox ViewOnly EditFieldRenderer EditSingleSelectReadOnly">
        {label ? (
          <div className="Item">
            <div className="Label">{label}</div>
          </div>
        ) : (
          <div className="NoItemsData">-</div>
        )}
      </div>

      {additionalComponent && selectedId && additionalComponent(selectedId)}
    </>
  );
};

export const singleMultiSelectReadOnlyFieldRenderer = (
  type:
    | "singleselect"
    | "multiselect"
    | "multiselectradio"
    | "multiselectOrderNumbers",
  additionalComponents?: AdditionalComponents,
  valueTypeStringFields?: string[]
) =>
  function singleMultiSelectReadOnlyFieldRenderer<T>(
    _key: React.ReactText,
    field: FormField<T>
  ) {
    let additionalComponent: AdditionalComponent | undefined = undefined;
    let additionalComponentSelectedIdFieldName: string | undefined = undefined;

    if (
      additionalComponents &&
      additionalComponents.hasOwnProperty(field.name) &&
      additionalComponents[field.name as keyof typeof additionalComponents] &&
      typeof additionalComponents[
        field.name as keyof typeof additionalComponents
      ] === "object" &&
      additionalComponents[
        field.name as keyof typeof additionalComponents
      ].hasOwnProperty("component") &&
      additionalComponents[
        field.name as keyof typeof additionalComponents
      ].hasOwnProperty("selectedIdFieldName")
    ) {
      additionalComponent =
        additionalComponents[field.name as keyof typeof additionalComponents]
          .component;
      additionalComponentSelectedIdFieldName =
        additionalComponents[field.name as keyof typeof additionalComponents]
          .selectedIdFieldName;
    }
    if (type === "singleselect") {
      return (
        <div
          key={`${String(field.name)}-read-only`}
          className="FormBox FormSideBySide"
        >
          <div className="FormLabel">
            <label>{field.label}</label>

            {field.toolTip && (
              <FormFieldTooltip
                tooltipText={field.toolTip}
                tooltipMainIconSource={tooltipMainIconSource}
                tooltipCloseIconSource={tooltipCloseIconSource}
              />
            )}
          </div>
          <div className="FormField">
            <EditSingleSelectReadOnly
              name={String(field.name)}
              dataSource={
                field.dataSource && typeof field.dataSource === "string"
                  ? pascalCaseToCamelCase(field.dataSource)
                  : null
              }
              valueTypeStringFields={valueTypeStringFields}
              additionalComponent={additionalComponent}
              additionalComponentSelectedIdFieldName={
                additionalComponentSelectedIdFieldName
              }
            />
          </div>
        </div>
      );
    }

    if (type === "multiselect") {
      return (
        <div key={"multiselect-read-only"} className="FormBox FormSideBySide">
          <div className="FormLabel">
            <label>{field.label}</label>

            {field.toolTip && (
              <FormFieldTooltip
                tooltipText={field.toolTip}
                tooltipMainIconSource={tooltipMainIconSource}
                tooltipCloseIconSource={tooltipCloseIconSource}
              />
            )}
          </div>
          <div className="FormField">
            <EditMultiSelectReadOnly
              name={String(field.name)}
              dataSource={
                field.dataSource && typeof field.dataSource === "string"
                  ? pascalCaseToCamelCase(field.dataSource)
                  : null
              }
              showUseRoleValues={field.name === "adminUserRoles" ? true : false}
              additionalComponent={additionalComponent}
              additionalComponentSelectedIdFieldName={
                additionalComponentSelectedIdFieldName
              }
            />
          </div>
        </div>
      );
    }

    if (type === "multiselectOrderNumbers") {
      return (
        <div key={"multiselect-read-only"} className="FormBox FormSideBySide">
          <div className="FormLabel">
            <label>{field.label}</label>

            {field.toolTip && (
              <FormFieldTooltip
                tooltipText={field.toolTip}
                tooltipMainIconSource={tooltipMainIconSource}
                tooltipCloseIconSource={tooltipCloseIconSource}
              />
            )}
          </div>
          <div className="FormField">
            <EditMultiSelectReadOnly
              name={String(field.name)}
              dataSource={
                field.dataSource && typeof field.dataSource === "string"
                  ? pascalCaseToCamelCase(field.dataSource)
                  : null
              }
              showUseRoleValues={field.name === "adminUserRoles" ? true : false}
              additionalComponent={additionalComponent}
              additionalComponentSelectedIdFieldName={
                additionalComponentSelectedIdFieldName
              }
            />
          </div>
        </div>
      );
    }

    if (type === "multiselectradio") {
      return (
        <div
          key={"multiselectradio-read-only"}
          className="FormBox FormSideBySide"
        >
          <div className="FormLabel">
            <label>{field.label}</label>

            {field.toolTip && (
              <FormFieldTooltip
                tooltipText={field.toolTip}
                tooltipMainIconSource={tooltipMainIconSource}
                tooltipCloseIconSource={tooltipCloseIconSource}
              />
            )}
          </div>
          <div className="FormField">
            <EditMultiSelectReadOnly
              name={String(field.name)}
              dataSource={
                field.dataSource && typeof field.dataSource === "string"
                  ? pascalCaseToCamelCase(field.dataSource)
                  : null
              }
              showUseRoleValues={field.name === "adminUserRoles" ? true : false}
            />
          </div>
        </div>
      );
    }
  };
type LinkConfig = {
  filterFieldName: string;
  filterOperator: "EQ" | "CONTAINS";
  filterValueFieldName: string;
  route: APP_ROUTES;
  text: string;
};

type LinkConfigs = {
  [fieldName: string]: LinkConfig;
};

export const singleMultiSelectWithLinkReadOnlyFieldRenderer = (
  linkConfigs: LinkConfigs
) =>
  function singleMultiSelectWithLinkReadOnlyFieldRenderer<T>(
    _key: React.ReactText,
    field: FormField<T>
  ) {
    return (
      <div key={`${String(field.name)}`} className="FormBox FormSideBySide">
        <div className="FormLabel">
          <label>{field.label}</label>

          {field.toolTip && (
            <FormFieldTooltip
              tooltipText={field.toolTip}
              tooltipMainIconSource={tooltipMainIconSource}
              tooltipCloseIconSource={tooltipCloseIconSource}
            />
          )}
        </div>
        <div className="FormField">
          <EditMultiSelectReadOnly
            name={String(field.name)}
            dataSource={
              field.dataSource && typeof field.dataSource === "string"
                ? pascalCaseToCamelCase(field.dataSource)
                : null
            }
            showUseRoleValues={field.name === "adminUserRoles" ? true : false}
            linkConfig={
              linkConfigs && linkConfigs.hasOwnProperty(field.name)
                ? linkConfigs[field.name as keyof typeof linkConfigs]
                : undefined
            }
          />
        </div>
      </div>
    );
  };
